class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    // Also clear is-empty from the cart-drawer custom element itself so
    // the items/footer aren't hidden by the is-empty CSS rules.
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    // SURGICAL UPDATE — never touch .cart-drawer-dynamic-buttons.
    //
    // The dynamic buttons container holds <shopify-accelerated-checkout-cart>
    // which in turn renders <shopify-apple-pay-button>, <shop-pay-wallet-button>
    // etc. Apple Pay in particular cannot survive a disconnect/reconnect cycle
    // on iOS Safari — Apple Pay merchant sessions are throttled and a second
    // bootstrap attempt within the same page silently fails. The only way to
    // keep Apple Pay alive across cart updates is to never remove its DOM node.
    //
    // So instead of replacing the whole #CartDrawer section via innerHTML, we
    // surgically swap just the parts that need to change: items list, upsell
    // carousel, and the bits of the footer that are NOT the wallet buttons.
    const freshSectionHTML = parsedState.sections && parsedState.sections['cart-drawer'];
    const liveDrawerInner = this.querySelector('.drawer__inner');
    if (freshSectionHTML && liveDrawerInner) {
      const freshDoc = new DOMParser().parseFromString(freshSectionHTML, 'text/html');
      const freshDrawerInner = freshDoc.querySelector('.drawer__inner');
      if (freshDrawerInner) {
        if (typeof window.surgicallyUpdateCartDrawerInner === 'function') {
          window.surgicallyUpdateCartDrawerInner(liveDrawerInner, freshDrawerInner);
        }
      }
    }

    // Update the cart icon bubble too (separate section, no wallet buttons in it)
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      try {
        bubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
      } catch (e) { /* ignore */ }
    }

    // Notify in-drawer widgets (cross-sell carousel, discount handler)
    // that the DOM has been replaced so they can re-bind to the new nodes.
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
